import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DischargeSummaryView } from "./admitted/DischargeSummaryView";

/**
 * Standalone printable page for a batch of discharge summaries.
 * Route: /print/discharges?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD&condition=X
 * Auto-fires window.print() once the summaries have rendered.
 */
export default function DischargesPrintPack() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";
  const condition = params.get("condition") || "All";

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      try {
        const { data } = await api.get("/admissions?status=Discharged");
        setItems(data || []);
      } finally { setLoading(false); }
    })();
  }, [authLoading, user]);

  const filtered = useMemo(() => {
    const from = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
    return items
      .filter((a) => {
        const dAt = a.discharged_at ? new Date(a.discharged_at).getTime() : 0;
        if (from && dAt < from) return false;
        if (to && dAt > to) return false;
        if (condition !== "All" && (a.discharge?.condition || "") !== condition) return false;
        return true;
      })
      .sort((a, b) => new Date(b.discharged_at || 0) - new Date(a.discharged_at || 0));
  }, [items, dateFrom, dateTo, condition]);

  useEffect(() => {
    if (!loading && filtered.length > 0) {
      const t = setTimeout(() => { try { window.print(); } catch {} }, 700);
      return () => clearTimeout(t);
    }
  }, [loading, filtered.length]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="ths-print-hide sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">Discharge Summary Pack</div>
          <div className="text-xs text-slate-500">
            {filtered.length} summar{filtered.length === 1 ? "y" : "ies"}
            {dateFrom ? ` · from ${dateFrom}` : ""}
            {dateTo ? ` · to ${dateTo}` : ""}
            {condition !== "All" ? ` · condition ${condition}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="print-pack-print-btn"
            onClick={() => window.print()}
            className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium hover:bg-[#1565C0]"
          >Print / Save as PDF</button>
          <button
            data-testid="print-pack-close-btn"
            onClick={() => window.close()}
            className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 border border-slate-200"
          >Close</button>
        </div>
      </div>

      <div className="ths-print-pack max-w-4xl mx-auto p-6 space-y-4" data-testid="print-pack-body">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Preparing summaries…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">No discharges match this filter.</div>
        ) : (
          filtered.map((a) => (
            <div key={a.admission_id} className="ths-pack-item bg-white rounded-xl border border-slate-200" data-testid={`pack-item-${a.admission_id}`}>
              <DischargeSummaryView admission={a} canAddendum={false} onAddendum={() => {}} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
